import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import SFDynamicForm from '../sf-dynamic-form/Index.js';
import { getWMSFields, validateFields } from '../../../utils/commonAPICalls.js';
import { useAuth } from '../../../wms/context/wms-context.js';
import LoadingSpinner from '../../../components/LoadingSpinner.js';
import config from '../../../utils/config.js';
import Notification from '../../../components/Notification.js';
import logError from '../../../utils/errorUtil.js';

export default function ReceivingPanel({ status, setStatus }) {
    const { currentUser, setCurrentUser } = useAuth();
    const ORIGIN = config.origin;

    // scroll to top of page
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [formData, setFormData] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [notification, setNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState({});

    const dialogRef = useRef(null);

    const effectiveRecord = currentUser.selectedMaidLine || currentUser.selectedMaid;

    const needsManifestId = !effectiveRecord?.Id;
    const fetchKey = useMemo(() => {
        const lineId = effectiveRecord?.Id || '';
        const manifestId = effectiveRecord?.inscor__Manifest__c || effectiveRecord?.Id || '';
        return `${status.receivingPanel ? 'open' : 'closed'}|${lineId}|${manifestId}`;
    }, [status.receivingPanel, effectiveRecord?.Id, effectiveRecord?.inscor__Manifest__c]);

    useEffect(() => {
        if (!status.receivingPanel || !effectiveRecord) return;
        if (needsManifestId && !effectiveRecord.inscor__Manifest__c) return;

        let cancelled = false;
        setStatus((s) => ({ ...s, loading: true }));
        setFormData(undefined);

        const payload = {
            manifestLine: { ...effectiveRecord },
            authentication: {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            },
            tableStructure: currentUser.tableStructures,
        };

        getWMSFields(payload, 'get-inventory-fields', 'get-dropdowns')
            .then((data) => {
                if (cancelled) return;
                setFormData(data);
                setStatus((s) => ({ ...s, loading: false }));
            })
            .catch((error) => {
                if (cancelled) return;
                logError(error, 'ReceivingPanel');
                setStatus((s) => ({ ...s, loading: false, error: true }));
            });

        return () => {
            cancelled = true;
        };
    }, [
        fetchKey,
        needsManifestId,
        status.receivingPanel,
        currentUser.authentication.userId,
        currentUser.authentication.userEmail,
        currentUser.authentication.userCompany,
        currentUser.tableStructures,
    ]);

    const handleSubmission = async () => {
        setIsLoading(true);

        const allFieldsValid = formData.find((field) => field.required && !field.value);

        // if there is a required field that is not filled out
        if (allFieldsValid) {
            let updatedFormData = formData.map((field) => {
                if (field.type === 'reference' && field.required && !field.value) {
                    return { ...field, forceError: true };
                }
                return { ...field, forceError: false };
            });
            setFormData(updatedFormData);

            setNotification(true);
            setNotificationMessage({
                status: 'error',
                title: `${allFieldsValid.label} Required`,
                message: `Please fill out the ${allFieldsValid.label} field before continuing.`,
            });
            setIsLoading(false);
            return;
        }

        let serverFieldValidation = await validateFields(
            formData,
            '/wms/receiving/validate-form',
            currentUser,
            currentUser?.selectedMaid?.Name === 'Multiple Manifests'
                ? currentUser?.selectedMaidLine?.manifestName
                : currentUser?.selectedMaid?.Name
        );

        if (serverFieldValidation.valid === false) {
            setNotification(true);
            setNotificationMessage({
                status: 'error',
                title: 'Error',
                message: serverFieldValidation.message,
            });

            setTimeout(() => {
                setNotification(false);
            }, 4000);

            return setIsLoading(false);
        }

        const dataToSend = {
            authentication: {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            },
            manifestLine: {
                ...(currentUser.selectedMaidLine || currentUser.selectedMaid),
            },
            formData,
        };
        setCurrentUser((u) => ({ ...u, copiedForm: formData }));

        if (serverFieldValidation.allowBinderCheck) {
            setStatus({ ...status, receivingPanel: false, binderCheckPanel: true });
        } else {
            setStatus({ ...status, receivingPanel: false, actionPanel: true });
        }
        fetch(`${ORIGIN}/wms/receiving/receive-manifest-line`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSend),
        })
            .then((res) => {
                if (!res.ok) {
                    if (res.status === 500) {
                        // Process the response to get the error message
                        return res.json().then((errorData) => {
                            console.log(errorData);
                            setStatus({
                                ...status,
                                loading: false,
                                error: true,
                                errorMessage: errorData.message || 'Internal Server Error',
                            });
                        });
                    }
                    // Handle other non-2xx responses if needed
                }
                return res.json();
            })
            .then((data) => {
                if (data?.status === 'success') {
                    setCurrentUser({
                        ...currentUser,
                        receiving_inventoryId: data.inventoryLine.Id,
                        receiving_inventoryLine: data.inventoryLine,
                    });
                    setIsLoading(false);
                }
            })
            .catch((err) => {
                logError(err, 'handleSubmission');
                setStatus({ ...status, loading: false, error: true });
            });
    };

    useEffect(() => {
        function handleBackdropClick(event) {
            if (dialogRef.current && dialogRef.current.contains(event.target)) {
                event.stopPropagation();
            }
        }

        document.addEventListener('click', handleBackdropClick);

        return () => {
            document.removeEventListener('click', handleBackdropClick);
        };
    }, []);

    const handleClose = (event) => {
        console.log('handleClose');
    };

    const handleCopyFromLast = () => {
        let copiedForm = currentUser.copiedForm;

        if (!copiedForm) {
            return; // If there's no previous form data, exit early.
        }

        // Create a helper function to perform a deep copy
        const deepClone = (value) => {
            if (Array.isArray(value)) {
                return value.map((item) => deepClone(item));
            } else if (value && typeof value === 'object') {
                let clonedObj = {};
                for (let key in value) {
                    clonedObj[key] = deepClone(value[key]);
                }
                return clonedObj;
            } else {
                return value;
            }
        };

        // Create the updated formData array
        let updatedFormData = formData.map((field) => {
            // Look for the corresponding field in copiedForm
            let copiedField = copiedForm.find((f) => f.name === field.name);

            // If found, and it has a value, then copy the value (deeply if necessary)
            if (copiedField && copiedField.value !== undefined) {
                return {
                    ...field, // Spread to retain other properties
                    value: deepClone(copiedField.value), // Deep clone the value
                };
            }
            return field; // If not found, return the original field
        });

        setFormData(updatedFormData);
    };

    return (
        <>
            <Transition.Root show={status.receivingPanel} as={Fragment}>
                <Dialog as='div' className='relative z-10' open={status.receivingPanel} onClose={handleClose}>
                    <Notification
                        showSavedNotification={notification}
                        setShowSavedNotification={setNotification}
                        title={notificationMessage.title}
                        desc={notificationMessage.message}
                        status={notificationMessage.status}
                        autoClose={true}
                    />
                    <Transition.Child
                        as={Fragment}
                        enter='ease-out duration-300'
                        enterFrom='opacity-0'
                        enterTo='opacity-100'
                        leave='ease-in duration-200'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'
                    >
                        <div className='fixed inset-0 bg-gray-500 bg-opacity-90 transition-opacity' />
                    </Transition.Child>

                    <div className='overflow-y-auto fixed inset-0 z-10'>
                        <div className='flex justify-center items-end p-4 text-center sm:items-center sm:p-0'>
                            <Transition.Child
                                as={Fragment}
                                enter='ease-out duration-300'
                                enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                                enterTo='opacity-100 translate-y-0 sm:scale-100'
                                leave='ease-in duration-200'
                                leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                                leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                            >
                                <Dialog.Panel className='relative text-left bg-white rounded-lg shadow-xl transition-all transform sm:my-8 sm:w-full sm:max-w-9xl min-h-96'>
                                    <div>
                                        <div className='text-center'>
                                            <Dialog.Title
                                                as='h3'
                                                className='p-2 py-4 mb-4 text-2xl font-medium leading-6 text-white rounded-t-md bg-blue-primary'
                                            >
                                                Receiving Part:
                                                {currentUser?.selectedMaidLine?.inscor__Part_Master__rName ? (
                                                    <span className='ml-4 underline'>
                                                        {currentUser.selectedMaidLine.inscor__Part_Master__rName}
                                                    </span>
                                                ) : (
                                                    currentUser?.receivingFormData?.PartNumber && (
                                                        <span className='ml-4 underline'>
                                                            {currentUser.receivingFormData.PartNumber}
                                                        </span>
                                                    )
                                                )}
                                            </Dialog.Title>
                                            {status.loading && <LoadingSpinner />}

                                            <div className='grid grid-cols-1 gap-2 p-8 md:grid-cols-2 lg:grid-cols-3'>
                                                {formData && <SFDynamicForm formData={formData} setFormData={setFormData} />}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex flex-row justify-end p-4 mt-8 space-x-4'>
                                        <button
                                            type='button'
                                            className='px-3 py-1.5 w-24 text-lg font-medium text-white bg-gray-500 rounded-md border border-transparent shadow-sm cursor-pointer hover:bg-gray-300 hover:text-blue-primary focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2'
                                            onClick={() => {
                                                setStatus({ ...status, receivingPanel: false });
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type='button'
                                            disabled={status.loading || !currentUser.copiedForm}
                                            className='px-3 py-1.5 w-48 text-lg font-medium text-white bg-gray-500 rounded-md border border-transparent shadow-sm cursor-pointer hover:bg-gray-300 hover:text-blue-primary focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2'
                                            onClick={() => {
                                                handleCopyFromLast();
                                            }}
                                        >
                                            Copy From last
                                        </button>
                                        <button
                                            type='button'
                                            disabled={isLoading}
                                            className='px-3 py-1.5 w-48 text-lg font-medium text-white rounded-md border border-transparent shadow-sm bg-blue-primary hover:bg-gray-300 hover:text-blue-primary focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2'
                                            onClick={() => {
                                                handleSubmission();
                                            }}
                                        >
                                            Receive Inventory
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    );
}
